import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import Resizer from 'react-image-file-resizer';
import 'react-image-crop/dist/ReactCrop.css';

import { CoreBox, CoreTypography } from '@youscience/core';
import type { School } from '@common-types';

import ActionButton from '@components/ActionButton';

import useHomePageStore from '@store/HomePageStore';
import { baseService } from '@services/base.service';
import { getBreadcrumbs } from '@utils/breadcrumbs';
import { CollegeLocation } from '@interfaces/location';
import { useDebouncedEffect } from '@hooks/useDebouncedEffect';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { API_BASE_URL } from '@constants/externalRoutes';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { SchoolCard } from '@components/SchoolCard';
import DialogFooter from '@components/DialogFooter';

import { canvasPreview } from './canvasPreview';

import {
  sxStyles,
  StyledCoreDialog,
  StyledDialogTitle,
  StyledHeadSectionActions,
  StyledHeadSectionInner,
  StyledHeadSectionTitle,
  StyledImgWrap,
  StyledSchoolPhotoWrap,
} from './imageCrop.styles';

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

interface ImageCropProps {
  school: School;
  file: globalThis.File | null;
  filename?: string;
  folder?: string;
  onClose: () => void;
  onSelectImage: () => void;
  onSaveImage: (name: string) => Promise<void>;
}

export const ImageCrop = (props: ImageCropProps) => {
  const { file, filename, folder, school, onClose, onSaveImage, onSelectImage } = props;

  const { directory } = school;

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const photoPreviewRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale] = useState(1);
  const [rotate] = useState(0);
  const [aspect] = useState<number | undefined>(527 / 340);
  const [extension, setExtension] = useState<string | undefined>(file ? file.name.split('.').pop() : undefined);

  const selectedSchoolName = useHomePageStore((state) => state.landingPageSchoolData?.cardInfo?.name);

  const location: CollegeLocation = useLocation();

  const pageBreadcrumbs = getBreadcrumbs(
    location.state?.from,
    BREADCRUMBS_PAGE_TEXT.SCHOOL_DETAILS,
    selectedSchoolName,
  );

  useEffect(() => {
    setCrop(undefined); // Makes crop preview update between images.
    setExtension(undefined);

    if (file) {
      const reader = new FileReader();

      setExtension(file.name.split('.').pop());

      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));

      reader.readAsDataURL(file);
    }
  }, [file]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget;

      setCrop(centerAspectCrop(width, height, aspect));
    }
  };

  const getDimensions = (file: globalThis.File): Promise<{ width: number; height: number }> =>
    new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.src = URL.createObjectURL(file);
    });

  const resizeFile = (file: globalThis.File): unknown =>
    new Promise((resolve) => {
      const processDimensions = async () => {
        const dimensions = await getDimensions(file);

        let quality = 100;

        if (dimensions.width > 800 || dimensions.height > 600) {
          quality = 50;
        }

        Resizer.imageFileResizer(
          file,
          800,
          600,
          file.type.split('/')[1],
          quality,
          0,
          (uri) => {
            resolve(uri);
          },
          'base64',
        );
      };
      processDimensions();
    });

  const base64ToBlob = (base64String: string, contentType = '') => {
    const byteCharacters = atob(base64String);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }

    const byteArray = new Uint8Array(byteArrays);

    return new Blob([byteArray], { type: contentType });
  };

  async function onUploadClick() {
    if (previewCanvasRef.current) {
      try {
        setIsLoading(true);
        const defaultFilename = `${filename ?? 'default'}-${new Date().getTime()}.${extension}`;

        const signedUrl = await baseService.get<unknown, { data: { url: string } }>(`${API_BASE_URL}/generate-url`, {
          params: {
            filename: defaultFilename,
            folder,
          },
        });

        const contentType = `image/${extension}`;
        const myHeaders = new Headers({ 'Content-Type': contentType });

        // TODO - Remove this if image processing is moved to server
        previewCanvasRef.current.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], defaultFilename, { type: contentType });
            const newFile = (await resizeFile(file)) as globalThis.File;

            // Convert Base64 to Blob
            const newBlob = base64ToBlob(
              (newFile as unknown as string).split(`data:${contentType};base64,`)[1],
              contentType,
            );

            await fetch(signedUrl.data.url, {
              method: 'PUT',
              headers: myHeaders,
              body: newBlob,
            });

            await onSaveImage(defaultFilename);
            setIsLoading(false);
            onClose();
          }
        }, contentType);
      } catch (e) {
        setIsLoading(false);
      }
    }
  }

  useDebouncedEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);

        if (photoPreviewRef.current) {
          canvasPreview(imgRef.current, photoPreviewRef.current, completedCrop, scale, rotate);
        }
      }
    },
    100,
    [completedCrop, scale, rotate],
  );

  const canvasProps = {};

  // 1366x768px

  return (
    <StyledCoreDialog
      open
      aria-labelledby="image-crop-title"
      onClose={onClose}
      maxWidth="xl"
      sx={{ overflow: 'hidden' }}
    >
      <CoreBox sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <CoreBox sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', overflow: 'hidden' }}>
          <StyledDialogTitle id="image-upload-title">Update Photo</StyledDialogTitle>

          <CoreBox sx={{ overflow: 'auto', height: '100%' }}>
            <CoreBox sx={{ display: 'flex', margin: '1rem', justifyContent: 'space-around' }}>
              <CoreBox sx={{ display: 'flex', flexDirection: 'column' }}>
                <CoreBox>
                  <CoreTypography variant="h5" component="h4">
                    Crop Image
                  </CoreTypography>
                </CoreBox>
                <CoreBox sx={{ maxWidth: '600px' }}>
                  {!!imgSrc && (
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={aspect}
                      minHeight={100}
                    >
                      <img
                        ref={imgRef}
                        alt="Crop me"
                        src={imgSrc}
                        style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                        onLoad={onImageLoad}
                      />
                    </ReactCrop>
                  )}
                </CoreBox>
                <CoreBox>
                  <ActionButton onClick={() => onSelectImage()}>Select Image</ActionButton>
                </CoreBox>
              </CoreBox>

              <CoreBox sx={{ display: 'flex', flexDirection: 'column' }}>
                <CoreBox>
                  <CoreTypography variant="h5" component="h4">
                    Card Preview
                  </CoreTypography>
                </CoreBox>
                <CoreBox style={{ position: 'relative' }}>
                  <SchoolCard
                    school={school}
                    schoolCardType="reduced"
                    photoPreview={
                      <CoreBox sx={canvasProps}>
                        <canvas
                          ref={photoPreviewRef}
                          style={{
                            border: '1px solid black',
                            objectFit: 'contain',
                            width: '100%',
                          }}
                        />
                      </CoreBox>
                    }
                  />
                  <CoreBox style={{ position: 'absolute', top: 0, width: '100%', height: '100%' }} />
                </CoreBox>
              </CoreBox>
            </CoreBox>

            <CoreBox sx={{ margin: '1rem' }}>
              <CoreBox>
                <CoreTypography variant="h5" component="h4">
                  School Details Preview
                </CoreTypography>
              </CoreBox>

              <CoreBox sx={sxStyles.headSectionBg}>
                <StyledHeadSectionInner>
                  <CoreBox sx={sxStyles.headSectionInfo}>
                    <Breadcrumbs readOnly links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

                    <StyledHeadSectionTitle variant="h1">{directory?.name}</StyledHeadSectionTitle>

                    <StyledHeadSectionActions />
                  </CoreBox>

                  <StyledSchoolPhotoWrap>
                    <StyledImgWrap>
                      {completedCrop ? (
                        <CoreBox sx={canvasProps}>
                          <canvas
                            ref={previewCanvasRef}
                            style={{
                              border: '1px solid black',
                              borderBottomLeftRadius: '240px',
                              objectFit: 'contain',
                              maxWidth: 527,
                              maxHeight: 340,
                            }}
                          />
                        </CoreBox>
                      ) : null}
                    </StyledImgWrap>
                  </StyledSchoolPhotoWrap>
                </StyledHeadSectionInner>
              </CoreBox>
            </CoreBox>
          </CoreBox>
        </CoreBox>

        <DialogFooter
          goBackButtonTitle="Cancel"
          goNextButtonTitle={isLoading ? 'Updating Photo' : 'Save Image'}
          onGoBack={onClose}
          onGoNext={onUploadClick}
          isDisableNextButton={isLoading}
        />
      </CoreBox>
      <CoreBox sx={{ height: '32px' }} />
    </StyledCoreDialog>
  );
};
